<template>
  <div class="glass-input" :class="{ 'glass-input--error': hasError, 'glass-input--disabled': disabled }" :data-testid="testId">
    <label v-if="label" :for="inputId" class="glass-input__label">
      {{ label }}
      <span v-if="required" class="glass-input__required">*</span>
    </label>
    
    <div class="glass-input__wrapper">
      <span v-if="icon" class="glass-input__icon glass-input__icon--left">
        <i :class="icon"></i>
      </span>
      
      <input
        v-if="type !== 'textarea'"
        :id="inputId"
        :type="currentType"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :min="min"
        :max="max"
        :step="step"
        :maxlength="maxlength"
        :inputmode="inputmode"
        class="glass-input__field"
        :class="{ 
          'glass-input__field--has-icon-left': icon,
          'glass-input__field--has-icon-right': iconRight || type === 'password'
        }"
        @input="$emit('update:modelValue', $event.target.value)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @keyup.enter="$emit('enter', $event)"
      />
      
      <textarea
        v-else
        :id="inputId"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :rows="rows"
        class="glass-input__field glass-input__field--textarea"
        :class="{ 'glass-input__field--has-icon-left': icon }"
        @input="$emit('update:modelValue', $event.target.value)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      ></textarea>
      
      <span 
        v-if="type === 'password'" 
        class="glass-input__icon glass-input__icon--right glass-input__icon--clickable"
        @click="togglePassword"
      >
        <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
      </span>
      
      <span v-else-if="iconRight" class="glass-input__icon glass-input__icon--right">
        <i :class="iconRight"></i>
      </span>
    </div>
    
    <transition name="slide-fade">
      <span v-if="hasError && errorMessage" class="glass-input__error">
        {{ errorMessage }}
      </span>
    </transition>
    
    <span v-if="hint && !hasError" class="glass-input__hint">
      {{ hint }}
    </span>
  </div>
</template>

<script>
let inputCounter = 0;

export default {
  name: 'GlassInput',
  emits: ['update:modelValue', 'blur', 'focus', 'enter'],
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: null
    },
    hint: {
      type: String,
      default: null
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    rows: {
      type: [Number, String],
      default: 4
    },
    min: {
      type: [Number, String],
      default: null
    },
    max: {
      type: [Number, String],
      default: null
    },
    step: {
      type: [Number, String],
      default: null
    },
    maxlength: {
      type: [Number, String],
      default: null
    },
    inputmode: {
      type: String,
      default: null
    },
    testId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      inputId: `glass-input-${++inputCounter}`,
      showPassword: false
    };
  },
  computed: {
    currentType() {
      if (this.type === 'password') {
        return this.showPassword ? 'text' : 'password';
      }
      return this.type;
    }
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    }
  }
};
</script>

<style scoped>
.glass-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.glass-input__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #424242;
}

.glass-input__required {
  color: #c3423f;
  margin-left: 2px;
}

.glass-input__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.glass-input__field {
  width: 100%;
  padding: 0.875rem 1rem;
  font-family: inherit;
  font-size: 1rem;
  color: #424242;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  outline: none;
  transition: all 0.3s ease;
}

.glass-input__field::placeholder {
  color: rgba(66, 66, 66, 0.5);
}

.glass-input__field:focus {
  border-color: var(--theme-primary, rgba(89, 169, 106, 0.6));
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 0 0 4px rgba(var(--theme-primary-rgb, 89, 169, 106), 0.1);
}

.glass-input__field--has-icon-left {
  padding-left: 3rem;
}

.glass-input__field--has-icon-right {
  padding-right: 3rem;
}

.glass-input__field--textarea {
  resize: vertical;
  min-height: 100px;
}

/* Icons */
.glass-input__icon {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 100%;
  color: rgba(66, 66, 66, 0.5);
  pointer-events: none;
}

.glass-input__icon--left {
  left: 0;
}

.glass-input__icon--right {
  right: 0;
}

.glass-input__icon--clickable {
  cursor: pointer;
  pointer-events: auto;
  transition: color 0.2s ease;
}

.glass-input__icon--clickable:hover {
  color: var(--theme-primary, #59a96a);
}

/* Error state */
.glass-input--error .glass-input__field {
  border-color: rgba(195, 66, 63, 0.6);
  background: rgba(195, 66, 63, 0.05);
}

.glass-input--error .glass-input__field:focus {
  box-shadow: 0 0 0 4px rgba(195, 66, 63, 0.1);
}

.glass-input__error {
  font-size: 0.8125rem;
  color: #c3423f;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.glass-input__error::before {
  content: '⚠';
  font-size: 0.75rem;
}

/* Hint */
.glass-input__hint {
  font-size: 0.8125rem;
  color: rgba(66, 66, 66, 0.6);
}

/* Disabled state */
.glass-input--disabled .glass-input__field {
  opacity: 0.6;
  cursor: not-allowed;
  background: rgba(0, 0, 0, 0.05);
}

/* Animations */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
